import type { Store } from "$lib/def/landing";
import type { ArrayElement, Join, Path } from "$lib/def/util-types";
import type { Location } from "$lib/serv/locations";
import type { GeoPoint } from "proto/address";
import type { DisplayTotals, Item, Order, Totals } from "proto/groceries_order";
import type { User } from "proto/user";

import { delete$, get$, post$, profile$, put$ } from "$lib/serv/core/auth";
import { allKeysStartingWith, getItem, observe, setItem } from "$lib/serv/core/local-store";
import { BehaviorSubject, Observable, Subject, buffer, catchError, debounceTime, delay, distinctUntilKeyChanged, filter, firstValueFrom, map, mergeScan, mergeWith, of, shareReplay, startWith, switchMap, tap, type ObservedValueOf, combineLatestWith, merge, defer, connectable, distinctUntilChanged, pairwise, finalize, Subscription } from "rxjs";
import { AlertType, toast } from "./toast";
import { loadBase, stores$ } from "./groc-landing";
import { pickAddress } from "$lib/ui/modals/AddressModal.svelte";
import { createCartAndStoreOutcome } from "./groc-store";

const KEY_HEADER = 'groc-cart-header';
// const KEY = 'groc-cart'; // old way of storing cart data

/**
 * Clear LS on logout. THIS IS THE ONLY USE OF profile$ IN THIS FILE AND SHOULD REMAIN SO TO KEEP FUNCTIONS TESTABLE.
 */
profile$.pipe(
	map(user => !!user),
	distinctUntilChanged(),
	pairwise(), // effectivley skipping the first emit
).subscribe(loggedin => !loggedin && clearAllLSCarts());

/**
 * When quantity updates are made to the cart, they are debounced for a short time before
 * being processed. This allows for multiple updates to be made in quick succession
 * without sending multiple requests to the server.
 * @param ms Wait time in milliseconds before sending a cart update to the server.
 */
export function setQuantityUpdateDebounce(ms = 1000) {
	_debounceTime = ms;
}
let _debounceTime = 1000;

export const DEFAULT_MERCHANT_KEY = 'pnp';
export function getLSCart(merchantId = DEFAULT_MERCHANT_KEY) {
	return observe<CartHeader>(`${KEY_HEADER}-${merchantId}`).pipe(
		map(ls => ls.value ?? {} as Partial<CartHeader>),
		distinctUntilKeyChanged('id'),
		switchMap(head => head?.id ? getCart(head.id) : of(undefined)),
		shareReplay(1),
	);
}

export function setLSCart(cart: CartData, merchantId = DEFAULT_MERCHANT_KEY) {
	setItem(`${KEY_HEADER}-${merchantId}`, {
		merchantId,
		id: cart?.uuid,
		expiresAt: cart ? cart?.updated_at_ts + cart?.cart_ttl_seconds * 1000 : 0,
		lastUpdated: cart?.updated_at_ts ?? 0,
	} as CartHeader);
}

export function clearAllLSCarts() {
	allKeysStartingWith(KEY_HEADER).forEach(key => setItem(key));
}

// TODO: add post-login function that transfers a current cart to a user and/or restores server-side cart(s)

// export async function restoreUserCart(merchantId: string = DEFAULT_MERCHANT_KEY) {
// 	const cart = await firstValueFrom(get$<CartData>(`ca://grocery/cart`));
// 	return setItem(`${KEY_HEADER}-${merchantId}`, {
// 		merchantId,
// 		id: cart.uuid,
// 		itemCount: cart.item_count,
// 		lastUpdated: Date.now(),
// 		expiresAt: cart.updated_at_ts + (cart.cart_ttl_seconds * 1000),
// 	}) as CartHeader;
// };

/**
 * Loading persisted cart data is now the responsability of the groc-store service. Once established 
 * the cart data is passed to the groc-cart service for further processing. The seed is the starting
 * state of the cart as established by the groc-store service.
 */
const subjCartSeed = new BehaviorSubject<Record<string, Partial<CartData>>>({});

const _cartSeed: Record<string, Partial<CartData>> = {};

/**
 * If a cart existed anywhere other than in this service, this is where it needs to be set.
 * @param cart The initial cart data to be built on.
 */
export function setCartSeed(cart: Partial<CartData>) {
	if (cart && !cart.merchantId) cart.merchantId = DEFAULT_MERCHANT_KEY;
	const merchantId = cart?.merchantId ?? DEFAULT_MERCHANT_KEY;
	_cartSeed[merchantId] = cart; // overwrite merchant cart seed
	subjCartSeed.next(_cartSeed);
}

// export function getCartSeed(merchantId: string = DEFAULT_MERCHANT_KEY) {
// 	return _cartSeed[merchantId];
// }

export function checkout(id: string) {
	return post$<Order>(`ca://grocery/cart/${id}/checkout`, {});
}

export function loadCart(uuid?:string) {
	return get$<CartData>(`ca://grocery/cart${uuid ? `/${uuid}` : ''}`).pipe(
		catchError(er => of(undefined)), // often 410: make a new cart
	);
}

const _migratedCarts = new Subject<CartData>();

export function migrateCart(cart:CartData, targetStoreId:string, location:Location) {
	const body = setCartInfo(cart, targetStoreId, location);
	return post$<CartData>(`ca://grocery/cart/${cart.uuid}/migrate`, body).pipe(
		tap(cart => _migratedCarts.next(cart)),
	);
}

/**
 * Take any cart and set the store and location on it.
 * @param cart Pre-existing cart info.
 * @param storeId Optional true store ID to set on the cart.
 * @param location Optional true location to set on the cart.
 * @returns Immutable cart with store and location set.
 */
export function setCartInfo(cart:Partial<CartData>, storeId?: string, location?: Location) {
	const clone = {...cart} as CartData;
	if (storeId) clone.store = { ...cart?.store, id: storeId} as Store;
	if (location) {
		clone.customer = { address: {} as Address};
		const adr = clone.customer.address;
		if (location.latitude || location.longitude) {
			adr.geo_location = {} as GeoPoint;
			if (location.latitude) adr.geo_location.lat = +location.latitude;
			if (location.longitude) adr.geo_location.lon = +location.longitude;
		}
		if (location.street_number) adr.street_number = location.street_number;
		if (location.street_name) adr.street_name = location.street_name;
		if (location.suburb) adr.suburb = location.suburb;
		if (location.town) adr.town = location.town;
		if (location.province) adr.province = location.province;
		if (location.postal_code) adr.postal_code = location.postal_code;
		if (location.building_type) adr.building_type = location.building_type;
		if (location.complex) adr.complex = location.complex;
		if (location.label) adr.label = location.label;
	}
	return clone;
}

/**
 * Extract the location from a cart as a partial Address.
 * @param cart An existing cart with a customer address.
 * @returns An address.
 */
export function getCartLocation(cart?:Partial<CartData>|null) {
	const adr = cart?.customer?.address as Location;
	if (!adr) return;
	const location = {} as Location;
	location.latitude = adr.latitude ?? adr.geo_location?.lat?.toString();
	location.longitude = adr.longitude ?? adr.geo_location?.lon?.toString();
	location.street_number = adr.street_number;
	location.street_name = adr.street_name;
	location.suburb = adr.suburb;
	location.label = adr.label;
	location.town = adr.town;
	location.province = adr.province;
	location.postal_code = adr.postal_code;
	return location;
}

const _user = new BehaviorSubject<{ store?: Pick<CartData['store'], 'id'>, customer?: Pick<CartData['customer'], 'address'>}>({});
/**
 * This sets the required parameters to create a cart when items get selected.
 * @param cartStub Specifically the customer (with address) and store (with id) properties.
 */
export function updateUser(cartStub: ObservedValueOf<typeof _user>) {
	_user.next(cartStub);
}

export function isValidCartLocation(location?:Location) {
	return !!(location && location.latitude?.trim() && location.longitude?.trim() && location.street_number?.trim() && location.street_name?.trim() && location.suburb?.trim());	
}

/**
 * @param location 
 * @param cartAddress 
 * @returns 
 */
export function locationEqual(location:Location, cartAddress: CartData['customer']['address']) {
	const eq = (a?:string, b?:string) => (a?.trim().toLowerCase() ?? '') === (b?.trim().toLowerCase() || '');
	return cartAddress?.geo_location?.lat === (location.latitude && +(location.latitude ?? ''))
	&& cartAddress?.geo_location?.lon === (location.longitude && +(location.longitude ?? ''))
	// && eq(cartAddress?.saved_delivery_instruction, location?.saved_delivery_instruction) // not stored server-side
	&& eq(cartAddress?.street_number, location?.street_number)
	&& eq(cartAddress?.street_name, location?.street_name)
	&& eq(cartAddress?.suburb, location?.suburb)
	&& eq(cartAddress?.town, location?.town)
	&& eq(cartAddress?.province, location?.province)
	&& eq(cartAddress?.postal_code, location?.postal_code);
	// && eq(cartAddress?.building_type, location?.building_type) // not stored server-side
	// && eq(cartAddress?.complex, location?.complex) // not stored server-side
	// && eq(cartAddress?.label, location?.label); // not stored server-side
}

const cartStreams = new Map<string, {sub:Subscription, $:Observable<Partial<CartData> | null>}>();

const qtyUpdate$ = new Subject<{
	merchantId:string,
	catalogue_key:string,
	quantity:number,
	alternative_item_catalogue_key?: string | null,
	withoutDebounce?: boolean,
}>();

/**
 * Add an item to the cart, adjust item quantities or remove an item (qty:0) from the
 * cart. Also set, update or remove alternative items.
 * @param merchantId `pnp`.
 * @param catalogue_key The cart item to modify the quantity or alternative item for. Empty string for a refresh.
 * @param quantity A new of unchanged quantity for the item. `0` to remove the item from
 * the cart.
 * @param alternative_item_catalogue_key The alternative item to set for the cart item.
 * `null` removed the alternative item. `undefined` leaves the alternative item
 * unchanged.
 * @param withoutDebounce If true, the update is processed immediately.
 */
export function updateCartItemQuantity(merchantId:string, catalogue_key:string, quantity = 0, alternative_item_catalogue_key?: string | null, withoutDebounce = false) {
	const data = { merchantId, catalogue_key, quantity, alternative_item_catalogue_key, withoutDebounce };
	qtyUpdate$.next(data);
}

const _realtimeUpdates = new Subject<{
	catalogue_key: string,
	quantity: number,
	alt_catalogue_key?: string,
	event: 'started' | 'started-unchanged' | 'ended',
	prevQuantity?: number, // only on 'started' and 'started-unchanged' event
	prevAlt?: string, // only on 'started' and 'started-unchanged' event
}>();
export function observeUpdateQtyRequests(catalogue_key?: string) {
	return _realtimeUpdates.pipe(
		filter(({catalogue_key: key}) => !catalogue_key || key === catalogue_key),
	)
}

export function getCartStream(
	merchantId: string = DEFAULT_MERCHANT_KEY,
) {
	if (cartStreams.has(merchantId)) {
		const { $ } = cartStreams.get(merchantId) as { $: Observable<Partial<CartData>> };
		return $;
	}
	
	const qtyUpdates$ = qtyUpdate$.pipe(
		filter(({merchantId: id}) => id === merchantId),
	);
	const merchQtyUpdates$ = qtyUpdates$.pipe(
		filter(up => !up.withoutDebounce),
	);
	const merchQtyUpdatesNoDealy$ = qtyUpdates$.pipe(
		filter(up => !!up.withoutDebounce),
	);

	const keyh = `${KEY_HEADER}-${merchantId}`;

	// Combine the seed with any UUID updates from local storage.
	const seed$ = subjCartSeed.pipe(
		map(seeds => seeds[merchantId] || {merchantId}),
		filter(({merchantId: id}) => id === merchantId),
		combineLatestWith(observe<CartHeader>(keyh).pipe(
			map(head => head.value ? { uuid: head.value.id } : {}),
		)),
		map(([cart, head]) => {
			cart = { ...cart }; // cloning is important for the distinctUntilKeyChanged operator
			if (!cart?.uuid && head?.uuid) cart.uuid = head.uuid;
			return cart;
		}),
	);

	const $ = seed$.pipe(
		distinctUntilKeyChanged('uuid'),
		delay(0), // not having a delay causes LS updates to execute a new mergeScan callback before the previous mergeScan callback could update accCart
		switchMap(cart => merge(
			merchQtyUpdates$.pipe(
				buffer(merchQtyUpdates$.pipe(debounceTime(_debounceTime))), // accumulate updates for X time then flush them
				filter(buffer => buffer.length > 0),
				map(qty => ({ cart, qty })),
			),
			merchQtyUpdatesNoDealy$.pipe(
				map(qty => ({ cart, qty: qty ? [qty] : [] })),
			),
			defer(() => of({ cart, qty: null })), // <- startwith
		)),
		mergeScan((accCart, {cart: seed, qty:updates}) => {
			if (seed.uuid) {
				if (!accCart || accCart.uuid !== seed.uuid) accCart = seed; // ls cart uuid set or changes
			} else if (accCart?.uuid) {
				// ls cart deleted (possibly post-logout)
				return of(null); // clear cart
			} else if (!updates) {
				return of(null); // no qty means no create cart
			} else {
				// use cart stub to set customer address and store id for the card creation
				accCart = { ..._user.value } as CartData;
			}
			updates?.filter(u => u.catalogue_key).forEach(u => {
				if (!u.catalogue_key) return;
				u.catalogue_key = translateCatalogueKey(u.catalogue_key, accCart?.store?.id);
				if (u.alternative_item_catalogue_key) {
					u.alternative_item_catalogue_key = translateCatalogueKey(u.alternative_item_catalogue_key, accCart?.store?.id);
				}
			});
			const { items, updateRequired } = mergeCartItems(accCart?.items, updates);
			return syncCartItems(accCart, updateRequired ? items : null, () => updateLS(keyh)).pipe(
				map(cart => cart ? Object.assign(accCart as CartData, cart) : null),
				mergeWith(_migratedCarts.pipe(filter(c => c.uuid === accCart?.uuid))),
				// tap(c => console.log('post merge', c)),
				tap(c => updateLS(keyh, c)),
			);
		}, null as Partial<CartData> | null),
		shareReplay(1),
	)

	cartStreams.set(merchantId, {sub: $.subscribe(), $});
	return $;
}

/**
 * A cart stream that considers the current local stores and only shows the cart if
 * applicable.
 * 
 * This function does not feel right. It is a hack to show the cart only if the store is
 * local. A merchant's cart may exists regardless of customer location, but it does not
 * make sense without its store being among the local stores.
 * 
 * A better solution would be to have the cart seed dictate the the cart emitted by
 * `getCartStream`.
 * 
 * @returns A stream of the current grocery cart.
 */
export function getLocalCartStream(merchantId: string = DEFAULT_MERCHANT_KEY,) {
	return getCartStream(merchantId).pipe(
		combineLatestWith(stores$.pipe(filter(({payload:p}) => !!p),map(({payload}) => payload))),
		map(([cart, stores]) => {
			if (!stores?.length) return null;
			if (~stores.findIndex(s => s.id === cart?.store?.id)) return cart; // cart store is local
			return null;
		}),
	);
}

export function getCartItem(catalogue_key: string): Observable<CartItem | null> {
  return getCartStream().pipe(map((c) => c?.items?.find((i) => i.catalogue_key === catalogue_key) ?? null))
}

export const cartItemQuantityGroc$ = getCartStream().pipe(
	map((cart) => cart?.items?.map((item) => item.quantity).reduce((a, b) => a + b, 0) || 0)
);

export function clearAllCartStreams() {
	cartStreams.clear();
}

const _mergeCartItems = (existingItems?: CartItem[] | null, updates?: {catalogue_key:string, quantity:number, alternative_item_catalogue_key?:string | null}[] | null) => {
	if (!updates) return { items: null, updateRequired: false };
	const updateMap = new Map<string, { quantity: number, alternative_item_catalogue_key?: string | null }>();
	updates.forEach(({catalogue_key, quantity, alternative_item_catalogue_key}) => {
		const existing = updateMap.get(catalogue_key);
		// we need to preserve the `null` alternative item value, `undefined` means no change (ignore)
		const alt = (alternative_item_catalogue_key === null || alternative_item_catalogue_key) ? alternative_item_catalogue_key : existing?.alternative_item_catalogue_key
		updateMap.set(catalogue_key, {quantity, alternative_item_catalogue_key: alt})
	});
	const items = [...(existingItems ?? [])] as ItemQty[];
	let updateRequired = false;
	updateMap.forEach(({quantity, alternative_item_catalogue_key}, catalogue_key) => {
		const idx = items.findIndex(item => item.catalogue_key === catalogue_key);
		if (~idx) { // item found
			const item = items[idx];
			if (item.quantity !== quantity || (typeof alternative_item_catalogue_key !== 'undefined' && item.alternative_item?.catalogue_key !== alternative_item_catalogue_key)) { // TODO: understand what alt. values mean on the server
				item._prevQuantity = item.quantity; // to be removed before sending to server as part of the realtime update
				item._prevAlt = item.alternative_item?.catalogue_key; // to be removed before sending to server as part of the realtime update
				item._changed = true; // to be removed before sending to server as part of the realtime update
				item.quantity = quantity;
				if (alternative_item_catalogue_key === null) {
					delete item.alternative_item;
				} else if (alternative_item_catalogue_key) {
					item.alternative_item = {
						catalogue_key: alternative_item_catalogue_key,
						quantity: 0,
					} as Item;
				}
				updateRequired = true;
			}
			// if (item.quantity <= 0) {
			// 	items.splice(idx);
			// 	updateRequired = true;
			// }
		} else {
			if (quantity) { // new item with quantity - simply add it
				items.push({
					catalogue_key,
					quantity,
					alternative_item: alternative_item_catalogue_key ? { catalogue_key: alternative_item_catalogue_key, quantity: 0 } as Item : undefined,
					_prevQuantity: 0, // to be removed before sending to server as part of the realtime update
					_prevAlt: undefined, // to be removed before sending to server as part of the realtime update
					_changed: true, // to be removed before sending to server as part of the realtime update
				});
				updateRequired = true;
			}
		}
	});
	return { items: items.length ? items : null, updateRequired };
};
let mergeCartItems = _mergeCartItems;
export const mergeCartItemQtys = _mergeCartItems;

/**
 * Synchronize the cart with the server. If the local cart has no UUID with quantities,
 * a new cart is created. If the local cart has a UUID, the server cart is updated with
 * the new quantities.
 * @param cartOpt Optional cart data to identify the cart to sync with.
 * @param items Item quantities to update the cart with.
 * @param clearLS If the server notifies the client of an abandoned cart, this function
 * is called to clear the local storage.
 * @returns A server-side cart with applied updates.
 */
const _syncCartItems = (
	cartOpt?: Partial<CartData> | null,
	quantities?: ItemQty[] | null,
	clearLS?: () => void,
):Observable<null | CartData> => {
	if (!cartOpt?.uuid && !quantities) return of(null);
	let cart = cartOpt ?? {} as Partial<CartData>;
	cart = { ...cart }; // let's leave the original cart untouched
	let $: Observable<CartData | null>;
	if (cart.uuid) {
		if (!quantities) {
			// no items to update, just get the cart while we're at it - there may be a server-side update
			$ = of(cart).pipe(
				tap(cart => cart.items?.forEach(item => {
					_realtimeUpdates.next({
						catalogue_key: item.catalogue_key,
						quantity: item.quantity,
						alt_catalogue_key: item.alternative_item?.catalogue_key,
						event: 'started-unchanged',
					});
				})),
				switchMap(cart => getCart(cart.uuid ?? '')), // uuid will have a value - checked above
				tap(cart => cart?.items?.forEach(item => {
					_realtimeUpdates.next({
						catalogue_key: item.catalogue_key,
						quantity: item.quantity,
						alt_catalogue_key: item.alternative_item?.catalogue_key,
						event: 'ended',
					});
				})),
				catchError(er => {
					if (er?.error_code === 'ABANDONED' || er?.error_code === 'NOT_FOUND') clearLS?.();
					return of(null);
				}),
			);
		} else if (cart.customer) { // if the cart has a customer it is no longer the LS cart (which is just a uuid)
			$ = setCart(cart, quantities).pipe( // update
				catchError(er => {
					if (er?.error_code === 'ABANDONED' || er?.error_code === 'NOT_FOUND') clearLS?.();
					noticeError(er);
					return of(cart as CartData);
				}),
			);
		} else {
			// we have not yet loaded the cart from the server, so we need to do that first before we can update it
			$ = getCart(cart.uuid).pipe(
				map(fresh => ({ ...fresh, uuid: cart?.uuid })), // the get-cart response has no uuid :facepalm: so add it
				switchMap(freshCart => setCart(freshCart, quantities)), // update
				catchError(er => {
					if (er?.error_code === 'ABANDONED' || er?.error_code === 'NOT_FOUND') {
						toast({ title:'Recreating cart...', type:AlertType.Info });
						delete cart.uuid;
						return setCart(cart, quantities ?? []);
					}
					return of(cart as CartData);
				}),
				catchError(er => {
					//clearLS?.();
					noticeError(er);
					return of(null);
				})
			)
		}
	} else {
		// a new server-side cart with correct quantities is created
		$ = setCart(cart, quantities ?? []).pipe( // create
			catchError(er => {
				noticeError(er);
				return of(null);
			})
		);
	}
	return $.pipe(
		map(fresh => (fresh && Object.assign(cart ?? {}, fresh)) as CartData)
	);
}

let syncCartItems = _syncCartItems;
/**
 * @inheritdoc _syncCartItems
*/
export const syncCartItemQtys = _syncCartItems;

const _getCart = (uuid: string):Observable<CartData | null> => {
	const req = new Request(`ca://grocery/cart/${uuid}`, { cache: 'default' });
	return get$<CartData>(req).pipe(
		map(fresh => ({
			...fresh,
			scheduled_fulfilment: fresh.scheduled_fulfilment ?? undefined, // force the property to be removed with `Object.assign` when not present
			uuid,
		} as CartData)), // cart response has no uuid
	)
}
let getCart = _getCart;

/**
 * Checks if item store matches cart store,
 * if not then returns an amended catalogue_key with cart store ID
 * @param catalogue_key 
 * @param storeId 
 * @returns new catalogue_key
 */
const translateCatalogueKey = (catalogue_key: string, storeId?: string) => {
	if (!storeId || catalogue_key.endsWith(`_${storeId}`)) return catalogue_key;
	return `${catalogue_key.slice(0, catalogue_key.lastIndexOf('_'))}_${storeId}`;
} 

const _setCart = (cart: Partial<CartData>, items:ItemQty[]) => {
	const url = 'ca://grocery/cart';
	const uuid = cart.uuid;

	const zq = (cart: {items: {quantity: number}[]}) => ({ // Zero-Quantity filter (FEW-730 we are not allowed to remove items simply by setting quantity to 0)
		...cart,
		items: cart.items.filter(item => item.quantity > 0),
	});

	return of({ uuid: cart.uuid, items, customer: cart.customer, store: cart.store }).pipe(
		tap(body => {
			body.items = items.map(item => {
				const base = { catalogue_key: item.catalogue_key, quantity: item.quantity, alternative_item: item.alternative_item as Item };
				const event = item._changed ? 'started' : 'started-unchanged';
				_realtimeUpdates.next({...base, event, prevQuantity: item._prevQuantity});
				return base;
			})//.filter(item => item.quantity > 0) // we depend on items remaining in the cart while their quantity is 0
		}),
		switchMap(body => body.uuid ? put$<CartData>(`${url}/${body.uuid}`, zq(body)) : post$<CartData>(url, zq(body))),
		tap(res => {
			items?.forEach(item => {
				_realtimeUpdates.next({catalogue_key: item.catalogue_key, quantity: item.quantity, alt_catalogue_key: item.alternative_item?.catalogue_key, event: 'ended'});
			})
		}),
		map(fresh => uuid ? { ...fresh, uuid } : fresh),
	)
}
let setCart = _setCart;


export function delCart(merchantId = DEFAULT_MERCHANT_KEY) {
	const keyh = `${KEY_HEADER}-${merchantId}`;
	const lastHead = getItem<CartHeader>(keyh);
	const $ = delete$(`ca://grocery/cart/${lastHead?.id}`);
	return $.pipe(
		catchError(er => {
			noticeError(er);
			return of({} as CartData); // restores cart for the ui
		}),
		finalize(() => setItem(keyh))
	);
}

function noticeError(er:any) {
	const error = er.error ?? er;
	if (error?.error_code === 'ABANDONED') {
		toast({ title: 'Grocery cart expired.', type: AlertType.Info });
	} else if (error?.error_code === 'NOT_FOUND') {
		toast({ title: 'Grocery cart no longer exists.', type: AlertType.Error });
	} else if (error?.error_code === 'INVALID_INPUT') {
		toast({ title: 'Invalid grocery cart modification.', type: AlertType.Error });
	} else {
		console.warn('noticeError', error);
		toast({ title: error?.friendly_message ?? er, type: AlertType.Error});
	}
}

const _updateLS = (keyh:string, c?: Partial<CartData> | null) => {
	if (!c) {
		setItem(keyh);
		return;
	}
	const lastHead = getItem<CartHeader>(keyh);
	if (lastHead?.id === c.uuid) return; // no change
	setItem(keyh, { ...lastHead, id: c.uuid });
}
let updateLS = _updateLS;

export function clearCart(cart: Partial<CartData>, merchantId = DEFAULT_MERCHANT_KEY) {
	cart.items?.forEach(item => {
		qtyUpdate$.next({merchantId, catalogue_key: item.catalogue_key, quantity: 0});
	})
}

type PlainAddress = Omit<Address, 'geo_location'>;

export function buildCartCustomer(location:Location, user?:User|null): Customer {
	const customer: Customer = {
		address: {
			street_number: location.street_number?.trim() || '',
			street_name: location.street_name?.trim() || '',
			suburb: location.suburb?.trim() || '',
			geo_location: {
				lat: +(location.latitude || ''),
				lon: +(location.longitude || ''),
			}
		}
	}
	const list = ['town', 'province', 'postal_code', 'building_type', 'complex', 'label', 'saved_delivery_instruction'] as (keyof PlainAddress)[];
	list.forEach((prop: keyof PlainAddress) => {
		const val = location[prop]?.trim();
		if (val) customer.address[prop] = val;
	});
	if (typeof user?.id !== 'undefined') customer.id = user.id;
	return customer;
}

export function hasAlcohol(cartItems: CartItem[] | undefined): boolean {
	if (!cartItems) return false;
	return cartItems.filter((item) => item.is_alcohol === true).length > 0;
}

// ############################################################
// ADD ALL TO CART (from anywhere, not just landing items)
// ############################################################

export async function addFrom(order:Order):Promise<{error?:string, location?:Location, storeId?:string}> {
	const curentCart = await firstValueFrom(getCartStream());
	let storeId = curentCart?.store?.id;
	let location = getCartLocation(curentCart);
	if (!storeId || !location) {
		// PROMPT ADDRESS AND CHECK IF IT'S SERVICABLE
		const picked = await pickAddress('Select a delivery address');
		if (!picked) return {};
		const $ = loadBase(picked.location, false).pipe(filter(l => !!l.payload),map(l => l.payload));
		const stores = await firstValueFrom($);
		if (!stores?.length) { // no stores means no delivery
			return {error: 'We do not currently service this area. Please select another delivery address.'};
		}
		const stores$ = of({ stores, location: picked.location });
		const profileCart$ = profile$.pipe(switchMap(u => u ? loadCart() : of(undefined)));
		const outcome = await firstValueFrom(createCartAndStoreOutcome(stores$, getLSCart(), profileCart$));
		storeId = outcome.cart.store?.id;
		location = getCartLocation(outcome.cart);
		updateUser(outcome.cart);
	}
	order.items.forEach(item => {
		updateCartItemQuantity(DEFAULT_MERCHANT_KEY, item.catalogue_key, item.quantity, item.alternative_item?.catalogue_key);
	});
	return {location, storeId};
}

// ############################################################
// MOCKING
// ############################################################

export function mock_mergeCartItems(f: typeof _mergeCartItems) {
	mergeCartItems = f;
}

export function mock_syncCartItems(f: typeof _syncCartItems) {
	syncCartItems = f;
}

export function mock_getCart(f: typeof _getCart) {
	getCart = f;
}

export function mock_setCart(f: typeof _setCart) {
	setCart = f;
}

export function mock_updateLS(f: typeof _updateLS) {
	updateLS = f;
}

// ############################################################
// DEFINITIONS
// ############################################################
export interface CartHeader {
	merchantId: string;
	id?: string;
	// storeId?: string; // let the server dictate this value
	// itemCount?: number; // cart badge needs to get the price total from the cart observable anyway
	// lastUpdated?: number; // not useful if the cart can be updated from another tab
	// expiresAt?: number; // as with `lastUpdated` not useful
}

type ItemQty = Pick<ArrayElement<CartData['items']>, 'catalogue_key' | 'quantity' | 'alternative_item'> & {
	_changed?: boolean,
	_prevQuantity?: number,
	_prevAlt?: string,
};

export interface CartRef {
	key: string;
}

export interface PromoBubble {
	text: string;
	type: string;
}

export interface CartData {
	uuid?: string;
	customer: Customer;
	store: Store;
	type?: string
	cart_type?: string
	items?: CartItem[]
	item_count?: number
	has_alcohol?: boolean
	display_totals?: DisplayTotals
	status?: string
	additional_fees?: any[]
	totals?: Totals
	can_check_out?: boolean
	alcohol_checkout_blocked?: boolean
	above_checkout_button_message?: string
	checkout_button_message?: string
	delivery_message?: string
	deliverability_updated_at_ts?: number
	created_at_ts: number
	updated_at_ts: number
	cart_ttl_seconds: number;
	delivery_status?: string;
	promotion: {
		promotional_bubble: PromoBubble[]
	}
	scheduled_fulfilment: {
		scheduled_fulfilment_ts: number, // epoch timestamp (seconds)
		scheduled_fulfilment_type: string, // Eg: "overnight"
	}
	// client-side only
	merchantId?: string;
}

type AddressProps = Join<Path<Required<Address>>, ".">

export const CART_LOCATION_PROPS: AddressProps[] = [
	'geo_location.lat',
	'geo_location.lon',
	'street_name',
	'street_number',
	'label',
	'suburb',
	'town',
	'building_type',
	'complex',
	'province',
	'postal_code',
];


export interface Address {
  geo_location: GeoPoint;
  street_number: string;
  street_name: string;
  suburb: string;
  town?: string;
  province?: string;
  postal_code?: string;
  building_type?: string;
  complex?: string;
  label?: string;
  saved_delivery_instruction?: string;
}

export interface Customer {
	id?: number;
	address: Address;
}

export interface Line {
	label: string
	value: number
	type?: string
}

export interface CartItem extends Item {
    [x: string]: unknown;
	subaisle_id?: number
	is_alcohol?: boolean
	in_stock?: boolean
	low_stock?: boolean
	maximum_order_quantity?: number
	disable_backup?:boolean
	was_total?:number
}

// export interface Store {
// 	id: string;
// 	name?: string
// 	description?: string
// 	delivery_fee?: number
// 	today_alcohol_hours?: {
// 		start: string;
// 		end: string;
// 		open: boolean;
// 		open_next_message: string;
// 	}
// }
